/* eslint-disable filenames/match-exported */
/* eslint-disable filenames/match-regex */

import { formatNumber } from '@/utils/number';

const bottomArrowWidth = 14;
const bottomArrowHeight = 4;

export default class ChartTooltip {
  #valueLabelSingular = null;
  #valueLabelPlural = null;
  bisect = null;
  element = null;
  color = 'black';
  textColor = 'white';
  position = 'top';
  #svg = null;
  #chartWidth = null;
  #chartHeight = null;
  #lastXValue = null;
  #lastYValue = null;

  showValueOnly = false;
  showBottomArrow = false;
  xPosOffset = null;
  yPosFixed = null;

  constructor(chart, valueLabelSingular = null, valueLabelPlural = null, options = {}) {
    this.#svg = chart.svg;
    this.#chartWidth = chart.width;
    this.#chartHeight = chart.height;
    this.#valueLabelSingular = valueLabelSingular;
    this.#valueLabelPlural = valueLabelPlural;

    const defaults = {
      showValueOnly: false,
      showBottomArrow: false,
      xPosOffset: null,
      yPosFixed: null,
    };

    options = Object.assign({}, defaults, options);
    this.showValueOnly = options.showValueOnly;
    this.showBottomArrow = options.showBottomArrow;
    this.xPosOffset = options.xPosOffset;
    this.yPosFixed = options.yPosFixed;
  }

  draw(x, y, xValue, yValue) {
    if (xValue === this.#lastXValue && yValue === this.#lastYValue) {
      return;
    }

    this.#lastXValue = xValue;
    this.#lastYValue = yValue;

    if (this.element === null) {
      this.element = this.#svg.append('g');
    }
    else {
      this.element.selectAll('*').remove();
    }

    const stroke = 5;
    const rect = this.element.append('rect')
      .attr('fill', this.color)
      .attr('stroke', this.color)
      .attr('stroke-width', 5)
      .attr('stroke-linejoin','round')
      .attr('width', 100)
      .attr('height', 42)
      .attr('rx', 5);

    let text = this.element.append('text')
      .attr('fill', this.textColor)
      .attr('dominant-baseline', 'middle')
      .attr('text-anchor', 'middle')
      .attr('x', 0)
      .attr('y', 0)
      .style('cursor', 'default');

    if (this.showValueOnly) {
      text = text
        .attr('font-weight', 'bold')
        .attr('style', 'font-size:12px')
        .text(this._getLabeledValue(yValue));
    }
    else {
      text = text
        .call(text => {
          text
            .selectAll('tspan')
            .data(() => [ xValue, this._getLabeledValue(yValue) ])
            .join('tspan')
            .attr('x', '0')
            .attr('y', (_, i) => `${1.3 + i * 1.25}em`)
            .attr('font-weight', (_, i) => i ? null : 'bold')
            .style('font-size', '8px')
            .text(d => d);
        });
    }

    this._autoSize(text, rect);

    const { width: w, height: h } = rect.node().getBBox();

    const distance = 10;
    let xPos = x - w / 2;
    let yPos = y - h;
    switch (this.position) {
      case 'top':
        yPos = y - h - distance - (this.showBottomArrow ? 4 : 0);
        break;

      case 'bottom':
        yPos = y + distance + (this.showBottomArrow ? 4 : 0);
        break;
    }

    const rightSide = x + (w / 2 + stroke / 2);
    const letfSide = x - (w / 2 + stroke / 2);
    const topSide = y - (h + stroke * 2);
    const bottomSide = y + (h + stroke * 2);

    // let bottomArrowPosition = 'center';

    if (rightSide > this.#chartWidth) {
      xPos -= rightSide - this.#chartWidth;
      // bottomArrowPosition = 'right';
    }
    else if (letfSide < 0) {
      xPos = stroke / 2;
      // bottomArrowPosition = 'left';
    }

    if (bottomSide > this.#chartHeight) {
      yPos -= bottomSide - this.#chartHeight + 3;
    }
    else if (topSide < 0) {
      yPos = 3;
    }

    if (this.xPosOffset !== null) {
      xPos += this.xPosOffset;
    }
    if (this.yPosFixed !== null) {
      yPos = this.yPosFixed;
    }

    if (this.showBottomArrow) {
      const arrowPosition = `translate(${(w / 2 - bottomArrowWidth / 2)}, ${h + (bottomArrowHeight / 2)})`;
      // if (bottomArrowPosition === 'left') {
      //   // This scale needed due to RECT has larger border radius than arrow could overlap!
      //   const scale = 2;
      //   const xAxisCompensationOfArrow = -0.5;
      //   arrowPosition = `translate(${stroke / 2 * -1 + xAxisCompensationOfArrow}, ${h + (bottomArrowWidth / 2 * scale)}) rotate(-90) scale(${scale})`;
      // } else if (bottomArrowPosition === 'right') {
      //   //
      // }

      this.element.append('path')
        .attr('class', 'tooltip-bottom-arrow')
        .attr('d', 'M13.9983 0.300781H0C5.5 1 5.75484 3.85 7 3.85C8.24516 3.85 8.5 1 13.9983 0.300781Z')
        .attr('fill', this.color)
        .attr('transform', arrowPosition);
    }

    this.element
      .attr('transform', 'translate(' + xPos + ', ' + yPos + ')')
      .style('display', null);
  }

  hide() {
    if (this.element !== null) {
      this.element.style('display', 'none');

      this.#lastXValue = null;
      this.#lastYValue = null;
    }
  }

  _getLabeledValue(value) {
    const parsedValue = parseFloat(value);

    let unit = null;
    if (this.#valueLabelSingular) {
      unit = this.#valueLabelSingular;

      if (this.#valueLabelPlural && parsedValue !== 1) {
        unit = this.#valueLabelPlural;
      }
    }

    if (unit && unit.length === 3 && unit.toUpperCase() === unit) {
      unit = ' ' + unit;
    }

    return formatNumber(parsedValue, unit, 2, true);
  }

  _autoSize(by, what) {
    const { width: w, height: h } = by.node().getBBox();
    // const { x, y, width: w, height: h } = by.node().getBBox();
    // what.attr('width', w + 2 * x);
    // what.attr('height', h + 2 * y);
    what.attr('width', w + 2 * 5);
    what.attr('height', h + 2 * 3);

    by.attr('x', what.attr('width') / 2);
    by.attr('y', Math.ceil(what.attr('height') / 2));

    by.selectAll('tspan').attr('x', what.attr('width') / 2);
  }
}
